import { useEffect, useState, useContext, useRef, } from "react";
import {
   Table,
   Button,
   Tag,
   Modal,
   Form,
   Input,
   Select,
   message,
   Upload,
   Space,
   Tooltip,
   Row,
   Col,
   Dropdown,
   Divider,
   Avatar,
   DatePicker,
} from "antd";
import {
   PlusOutlined,
   EditOutlined,
   DeleteOutlined,
   UploadOutlined,
   DownloadOutlined,
   EyeOutlined,
   UserOutlined,
   TeamOutlined,
   SearchOutlined,
   ReloadOutlined,
   ClearOutlined,
   MoreOutlined,
   CheckCircleOutlined,
   DollarOutlined
} from "@ant-design/icons";
import InfiniteScroll from 'react-infinite-scroll-component';
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

// ─── API Imports ───
import {
   getLeads,
   createLead,
   updateLead,
   deleteLead,
   importLeads,
   exportLeads,
   convertLead,
   bulkAssignLeads,
   getProjects,
   getUsersList
} from "../../services/leadsApi";
import dayjs from "dayjs";

const Leads = () => {
   const navigate = useNavigate();
   const { user } = useContext(AuthContext);
   const [leads, setLeads] = useState([]);
   const [open, setOpen] = useState(false);
   const [editingLead, setEditingLead] = useState(null);
   const [statusFilter, setStatusFilter] = useState(null);
   const [total, setTotal] = useState(0);
   const [currentPage, setCurrentPage] = useState(1);
   const [hasMore, setHasMore] = useState(true);
   const [usersList, setUsersList] = useState([]);
   const [loading, setLoading] = useState(false);
   const [convertOpen, setConvertOpen] = useState(false);
   const [convertLeadData, setConvertLeadData] = useState(null);
   const [searchText, setSearchText] = useState("");
   const [searchInput, setSearchInput] = useState("");
   const [projects, setProjects] = useState([]);
   const [projectFilter, setProjectFilter] = useState(null);
   const [dateFilter, setDateFilter] = useState(null);
   const [fromDate, setFromDate] = useState(null);
   const [toDate, setToDate] = useState(null);
   const [selectedRowKeys, setSelectedRowKeys] = useState([]);
   const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
   const [bulkAssignUser, setBulkAssignUser] = useState(null);
   const [assignmentFilter, setAssignmentFilter] = useState(null);
   const [form] = Form.useForm();
   const [convertForm] = Form.useForm();


   const requestIdRef = useRef(0);
   const isMountedRef = useRef(true);

   const statusOptions = ["New", "Contacted", "Qualified", "Proposal Sent", "Closed Won", "Closed Lost"];

   const statusConfig = {
      "New": { color: "#1890ff", bg: "#e6f7ff", border: "#91d5ff" },
      "Contacted": { color: "#fa8c16", bg: "#fff7e6", border: "#ffd591" },
      "Qualified": { color: "#722ed1", bg: "#f9f0ff", border: "#d3adf7" },
      "Proposal Sent": { color: "#13c2c2", bg: "#e6fffb", border: "#87e8de" },
      "Closed Won": { color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f" },
      "Closed Lost": { color: "#f5222d", bg: "#fff1f0", border: "#ffa39e" }
   };

   const columns = [
      {
         title: <span className="text-xs font-medium text-gray-500">#</span>,
         key: "index",
         width: 45,
         align: "center",
         render: (_, __, index) => (
            <span className="text-xs text-gray-400">
               {index + 1}
            </span>
         ),
      },

      {
         title: <span className="text-xs font-medium text-gray-600">Lead Name</span>,
         dataIndex: "name",
         key: "name",
         width: 180,
         ellipsis: true,
         render: (name) => (
            <Tooltip title={name || "-"} placement="top">
               <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-gray-800 text-sm truncate">
                     {name || "-"}
                  </span>
               </div>
            </Tooltip>
         )
      },

      {
         title: <span className="text-xs font-medium text-gray-600">Phone</span>,
         dataIndex: "phone",
         key: "phone",
         width: 150,
         ellipsis: true,
         render: (phone) => (
            <Tooltip title={phone || "-"} placement="top">
               <div className="flex items-center gap-1 text-gray-600 text-sm truncate">
                  {/* <PhoneOutlined
                  style={{ fontSize: "11px", flexShrink: 0 }}
               /> */}
                  <span className="truncate">
                     {phone || "-"}
                  </span>
               </div>
            </Tooltip>
         )
      },

      {
         title: <span className="text-xs font-medium text-gray-600">City</span>,
         dataIndex: "city",
         key: "city",
         width: 120,
         ellipsis: true,
         render: (value) => (
            <Tooltip title={value || "-"} placement="top">
               <div className="text-gray-600 text-sm truncate">
                  {value || "-"}
               </div>
            </Tooltip>
         )
      },

      {
         title: <span className="text-xs font-medium text-gray-600">Requirement</span>,
         dataIndex: "requirement",
         key: "requirement",
         width: 220,
         ellipsis: true,
         render: (value) => (
            <Tooltip title={value || "-"} placement="top">
               <div className="text-gray-600 text-sm truncate">
                  {value || "-"}
               </div>
            </Tooltip>
         )
      },

      {
         title: <span className="text-xs font-medium text-gray-600">Source</span>,
         dataIndex: "source",
         key: "source",
         width: 140,
         ellipsis: true,
         render: (value) => (
            <Tooltip title={value || "-"} placement="top">
               <div className="text-gray-600 text-sm truncate">
                  {value || "-"}
               </div>
            </Tooltip>
         )
      },

      {
         title: <span className="text-xs font-medium text-gray-600">Status</span>,
         dataIndex: "status",
         key: "status",
         width: 130,
         render: (status) => (
            <Tooltip title={status || "-"} placement="top">
               <div
                  className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium"
                  style={{
                     backgroundColor: statusConfig[status].bg,
                     color: statusConfig[status].color,
                     border: `1px solid ${statusConfig[status].border}`
                  }}
               >
                  {status}
               </div>
            </Tooltip>
         )
      },

      {
         title: <span className="text-xs font-medium text-gray-600">Project</span>,
         dataIndex: "project_name",
         key: "project_name",
         width: 180,
         ellipsis: true,
         render: (value) => value ? (
            <Tooltip title={value} placement="top">
               <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded-md text-xs max-w-full">

                  <span className="truncate">
                     {value}
                  </span>
               </div>
            </Tooltip>
         ) : (
            <span className="text-xs text-gray-400">
               Not assigned
            </span>
         )
      },

      {
         title: <span className="text-xs font-medium text-gray-600">Assigned To</span>,
         dataIndex: "assigned_user",
         key: "assigned_user",
         width: 160,
         ellipsis: true,
         render: (value) => value ? (
            <Tooltip title={value} placement="top">
               <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-600 rounded-md text-xs max-w-full">
                  <UserOutlined
                     style={{ fontSize: "10px", flexShrink: 0 }}
                  />
                  <span className="truncate">
                     {value}
                  </span>
               </div>
            </Tooltip>
         ) : (
            <span className="text-xs text-gray-400">
               Not assigned
            </span>
         )
      },

      {
         title: <span className="text-xs font-medium text-gray-600">Created By</span>,
         dataIndex: "created_by_name",
         key: "created_by_name",
         width: 150,
         ellipsis: true,
         render: (value) => (
            <Tooltip title={value || "-"} placement="top">
               <span className="text-gray-500 text-xs truncate block">
                  {value || "-"}
               </span>
            </Tooltip>
         )
      },
      {
         title: <span className="text-xs font-medium text-gray-600">Date</span>,
         dataIndex: "created_at",
         key: "created_at",
         width: 150,
         ellipsis: true,
         render: (date) => (
            <span className="text-gray-500 text-xs truncate block">
               {dayjs(date).format("DD/MM/YYYY")}
            </span>
         )
      },

      {
         title: <span className="text-xs font-medium text-gray-600">Actions</span>,
         key: "action",
         fixed: "right",
         width: 150,
         align: "center",

         render: (_, record) => {
            const isConverted =
               record.status === "Closed Won" && record.is_converted;

            const canConvert =
               record.status === "Closed Won" && !record.is_converted;

            const canEdit =
               user?.role === "admin" ||
               (record.assigned_to === user?.id &&
                  record.status !== "Closed Won");

            const canDelete = user?.role === "admin";

            const menuItems = [
               {
                  key: "view",
                  label: "View Details",
                  icon: <EyeOutlined />,
                  onClick: () => navigate(`/leads/${record.id}`)
               },

               ...(canConvert ? [{
                  key: "convert",
                  label: "Convert to Sale",
                  icon: <DollarOutlined />,
                  onClick: () => handleOpenConvertModal(record),
                  className: "text-green-600"
               }] : []),

               ...(canEdit ? [{
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: () => handleOpenEditModal(record)
               }] : []),

               ...(canDelete ? [
                  { type: "divider" },
                  {
                     key: "delete",
                     label: "Delete",
                     icon: <DeleteOutlined />,
                     danger: true,
                     onClick: () => {
                        Modal.confirm({
                           title: "Delete Lead?",
                           content: "This action cannot be undone.",
                           okText: "Delete",
                           okType: "danger",
                           onOk: () => handleDelete(record.id)
                        });
                     }
                  }
               ] : [])
            ];

            if (isConverted) {
               return (
                  <div className="flex items-center justify-center gap-2">
                     <Tag color="success" style={{ margin: 0 }}>
                        <CheckCircleOutlined /> Converted
                     </Tag>

                     <Tooltip title="View Details">
                        <Button
                           type="text"
                           size="small"
                           icon={<EyeOutlined />}
                           onClick={() =>
                              navigate(`/leads/${record.id}`)
                           }
                        />
                     </Tooltip>

                     <Tooltip title="More Actions">
                        <Dropdown
                           menu={{ items: menuItems }}
                           trigger={["click"]}
                        >
                           <Button
                              type="text"
                              size="small"
                              icon={<MoreOutlined />}
                           />
                        </Dropdown>
                     </Tooltip>
                  </div>
               );
            }

            return (
               <Space size="small">
                  {canConvert && (
                     <Tooltip title="Convert this lead">
                        <Button
                           type="primary"
                           size="small"
                           onClick={() =>
                              handleOpenConvertModal(record)
                           }
                           style={{ fontSize: "11px" }}
                        >
                           Convert
                        </Button>
                     </Tooltip>
                  )}

                  <Tooltip title="More Actions">
                     <Dropdown
                        menu={{ items: menuItems }}
                        trigger={["click"]}
                     >
                        <Button
                           type="text"
                           size="small"
                           icon={<MoreOutlined />}
                        />
                     </Dropdown>
                  </Tooltip>
               </Space>
            );
         }
      }
   ];


   const hasActiveFilters = statusFilter || projectFilter || searchText || assignmentFilter || dateFilter;

   const rowSelection = {
      selectedRowKeys,
      onChange: (keys) => setSelectedRowKeys(keys),
      fixed: "left",
      getCheckboxProps: (record) => ({ disabled: !!record.assigned_to })
   };

   useEffect(() => {
      isMountedRef.current = true;
      return () => { isMountedRef.current = false; };
   }, []);

   const fetchLeads = async (page = 1, append = false) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);

      try {
         const params = {
            page,
            limit: 10,
            status: statusFilter || undefined,
            project_id: projectFilter || undefined,
            search: searchText || undefined,
            assigned: assignmentFilter || undefined,
            from_date: fromDate ? fromDate.format("YYYY-MM-DD") : undefined,
            to_date: toDate ? toDate.format("YYYY-MM-DD") : undefined,
         };

         // Clean undefined values
         Object.keys(params).forEach(key => {
            if (params[key] === undefined) delete params[key];
         });

         const res = await getLeads(params);

         if (requestId !== requestIdRef.current) {
            return;
         }

         if (isMountedRef.current) {
            if (append) {
               setLeads(prev => {
                  const existingIds = new Set(prev.map(item => item.id));
                  const newItems = res.data.data.filter(item => !existingIds.has(item.id));
                  return [...prev, ...newItems];
               });
            } else {
               setLeads(res.data.data);
            }

            setTotal(res.data.total);
            setCurrentPage(page);

            const totalPages = Math.ceil(res.data.total / 10);
            setHasMore(page < totalPages);
         }
      } catch (err) {
         if (requestId === requestIdRef.current && isMountedRef.current) {
            message.error("Failed to fetch leads");
            setHasMore(false);
         }
      } finally {
         if (requestId === requestIdRef.current && isMountedRef.current) {
            setLoading(false);
         }
      }
   };

   const fetchMoreData = () => {
      if (!loading && hasMore) {
         fetchLeads(currentPage + 1, true);
      }
   };

   useEffect(() => {
      setLeads([]);
      setCurrentPage(1);
      setHasMore(true);

      const timer = setTimeout(() => {
         fetchLeads(1, false);
      }, 100);

      return () => clearTimeout(timer);
   }, [statusFilter, projectFilter, searchText, assignmentFilter, fromDate,
      toDate]);

   const fetchProjectsData = async () => {
      try {
         const res = await getProjects();
         if (isMountedRef.current) setProjects(res.data);
      } catch (err) {
         console.error(err);
      }
   };

   useEffect(() => { fetchProjectsData(); }, []);

   useEffect(() => {
      if (user?.role !== "admin") return;
      getUsersList().then(res => {
         if (isMountedRef.current) setUsersList(res.data);
      }).catch(console.error);
   }, [user?.role]);

   const handleSubmit = async (values) => {
      setLoading(true);
      try {
         if (editingLead) {
            await updateLead(editingLead.id, values);
            message.success("Lead updated successfully");
         } else {
            await createLead(values);
            message.success("Lead added successfully");
         }
         handleCloseModal();
         setLeads([]);
         setCurrentPage(1);
         setHasMore(true);
         setTimeout(() => fetchLeads(1, false), 100);
         fetchProjectsData();
      } catch (err) {
         message.error(editingLead ? "Update failed" : "Add failed");
      } finally {
         setLoading(false);
      }
   };

   const handleDelete = async (id) => {
      setLoading(true);
      try {
         await deleteLead(id);
         message.success("Lead deleted successfully");
         setLeads([]);
         setCurrentPage(1);
         setHasMore(true);
         setTimeout(() => fetchLeads(1, false), 100);
         fetchProjectsData();
      } catch (err) {
         message.error("Delete failed");
      } finally {
         setLoading(false);
      }
   };

   const handleImport = (file) => {
      const formData = new FormData();
      formData.append("file", file);
      setLoading(true);
      importLeads(formData)
         .then(res => {
            const s = res.data.summary;
            message.success(`Import successful! Inserted: ${s.inserted}, Updated: ${s.updated}, Skipped: ${s.skipped}`);
            setLeads([]);
            setCurrentPage(1);
            setHasMore(true);
            setTimeout(() => fetchLeads(1, false), 100);
            fetchProjectsData();
         })
         .catch(() => message.error("Import failed"))
         .finally(() => setLoading(false));
      return false;
   };

   // const handleExport = async () => {
   //    try {
   //       setLoading(true);
   //       const response = await exportLeads();
   //       const url = window.URL.createObjectURL(new Blob([response.data]));
   //       const link = document.createElement("a");
   //       link.href = url;
   //       link.setAttribute("download", "Leads.xlsx");
   //       document.body.appendChild(link);
   //       link.click();
   //       link.remove();
   //       message.success("Export successful");
   //    } catch (error) {
   //       message.error("Export failed");
   //    } finally {
   //       setLoading(false);   
   //    }
   // };

   const handleExport = async () => {
      try {
         setLoading(true);

         const response = await exportLeads({
            status: statusFilter || undefined,
            project_id: projectFilter || undefined,
            assigned: assignmentFilter || undefined,
            search: searchText || undefined,

            from_date: fromDate
               ? fromDate.format("YYYY-MM-DD")
               : undefined,

            to_date: toDate
               ? toDate.format("YYYY-MM-DD")
               : undefined,
         });

         const url = window.URL.createObjectURL(
            new Blob([response.data])
         );

         const link = document.createElement("a");
         link.href = url;
         link.setAttribute("download", "Leads.xlsx");

         document.body.appendChild(link);
         link.click();
         link.remove();

         window.URL.revokeObjectURL(url);

         message.success("Export successful");

      } catch (error) {

         let errorMessage = "Export failed";

         if (error.response?.data instanceof Blob) {
            try {
               const text = await error.response.data.text();
               const data = JSON.parse(text);

               errorMessage = data.message || errorMessage;
            } catch (err) {
               // Default message
            }
         } else {
            errorMessage =
               error.response?.data?.message || errorMessage;
         }

         message.error(errorMessage);

      } finally {
         setLoading(false);
      }
   };

   const handleConvert = async (values) => {
      if (!convertLeadData) return;
      setLoading(true);
      try {
         await convertLead(convertLeadData.id, values);
         message.success("Lead converted successfully");
         handleCloseConvertModal();
         setLeads([]);
         setCurrentPage(1);
         setHasMore(true);
         setTimeout(() => fetchLeads(1, false), 100);
      } catch (err) {
         message.error("Conversion failed");
      } finally {
         setLoading(false);
      }
   };

   const handleCloseModal = () => { setOpen(false); setEditingLead(null); form.resetFields(); };
   const handleCloseConvertModal = () => { setConvertOpen(false); setConvertLeadData(null); convertForm.resetFields(); };
   const handleOpenEditModal = (record) => { setEditingLead(record); setOpen(true); setTimeout(() => form.setFieldsValue(record), 0); };
   const handleOpenAddModal = () => { setEditingLead(null); form.resetFields(); setOpen(true); };
   const handleOpenConvertModal = (record) => { setConvertLeadData(record); setConvertOpen(true); };

   const handleSearch = (value) => { setSearchText(value.trim()); setSearchInput(value.trim()); };
   const handleSearchInputChange = (e) => { setSearchInput(e.target.value); if (!e.target.value) setSearchText(""); };

   const handleBulkAssign = async () => {
      if (!bulkAssignUser) { message.warning("Please select a user"); return; }
      try {
         setLoading(true);
         const res = await bulkAssignLeads({ leadIds: selectedRowKeys, assigned_to: bulkAssignUser });
         message.success(`Successfully assigned ${res.data.updated} leads`);
         setBulkAssignOpen(false);
         setSelectedRowKeys([]);
         setBulkAssignUser(null);
         setLeads([]);
         setCurrentPage(1);
         setHasMore(true);
         setTimeout(() => fetchLeads(1, false), 100);
      } catch (err) {
         message.error("Assignment failed");
      } finally {
         setLoading(false);
      }
   };


   const handleClearFilters = () => {
      setLeads([]);
      setStatusFilter(null);
      setProjectFilter(null);
      setSearchText("");
      setSearchInput("");
      setAssignmentFilter(null);
      setCurrentPage(1);
      setHasMore(true);
   };

   return (
      <div className="bg-gray-50 min-h-screen">
         <style>{`
            .custom-card {
               background: #fff;
               border-radius: 10px;
               box-shadow: 0 1px 2px 0 rgba(0,0,0,0.03), 0 1px 3px 1px rgba(0,0,0,0.04);
               border: 1px solid #f3f4f6;
            }
            .custom-table .ant-table-thead > tr > th {
               background: #f9fafb;
               border-bottom: 1px solid #e5e7eb;
               font-weight: 500;
               padding: 10px 16px;
            }
            .custom-table .ant-table-tbody > tr > td {
               padding: 12px 16px;
               border-bottom: 1px solid #f3f4f6;
            }
            .custom-table .ant-table-tbody > tr:hover > td {
               background: #f9fafb;
            }
            .action-btn {
               border-radius: 6px;
               font-size: 13px;
               height: 32px;
               display: inline-flex;
               align-items: center;
               gap: 6px;
               font-weight: 500;
               transition: all 0.2s;
            }
            .action-btn:hover {
               transform: translateY(-1px);
               box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .filter-select .ant-select-selector {
               border-radius: 6px !important;
               border: 1px solid #e5e7eb !important;
            }
            .filter-select.ant-select-focused .ant-select-selector {
               border-color: #6366f1 !important;
               box-shadow: 0 0 0 2px rgba(99,102,241,0.1) !important;
            }
            .search-input .ant-input-affix-wrapper {
               border-radius: 6px;
               border: 1px solid #e5e7eb;
            }
            .search-input .ant-input-affix-wrapper:hover,
            .search-input .ant-input-affix-wrapper:focus {
               border-color: #6366f1;
            }
            .modal-form .ant-form-item {
               margin-bottom: 16px;
            }
            .modal-form .ant-input,
            .modal-form .ant-select-selector {
               border-radius: 6px;
            }
            .stats-badge {
               background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
               color: white;
               padding: 2px 8px;
               border-radius: 10px;
               font-size: 11px;
               font-weight: 600;
            }
            .infinite-scroll-component::-webkit-scrollbar {
               width: 6px;
            }
            .infinite-scroll-component::-webkit-scrollbar-track {
               background: #f1f1f1;
               border-radius: 10px;
            }
            .infinite-scroll-component::-webkit-scrollbar-thumb {
               background: #888;
               border-radius: 10px;
            }
            .infinite-scroll-component::-webkit-scrollbar-thumb:hover {
               background: #555;
            }
         `}</style>

         <div className="bg-white border-b border-gray-200 px-6 py-1">
            <div className="flex items-center justify-between">
               <div>
                  <h1 className="text-1xl font-semibold text-gray-900 m-0 p-0">Leads Management</h1>
                  <p className="text-sm text-gray-500 mt-0.5">
                     Manage and track all your leads efficiently
                  </p>
               </div>
               <div className="flex items-center gap-3">
                  <div className="text-right mr-4">
                     <div className="text-xs text-gray-500">Total Leads</div>
                     <div className="text-1.2xl font-bold text-gray-900">{total}</div>
                  </div>
                  {selectedRowKeys.length > 0 && (
                     <div className="text-right mr-4 px-4 py-2 bg-indigo-50 rounded-lg">
                        <div className="text-xs text-indigo-600">Selected</div>
                        <div className="text-1.2xl font-bold text-indigo-600">{selectedRowKeys.length}</div>
                     </div>
                  )}
               </div>
            </div>
         </div>

         <div className="p-1">
            <div className="custom-card p-1 mb-1">
               <div className="flex flex-col">
                  <Row gutter={[12, 12]} align="middle">
                     <Col xs={24} sm={12} md={6}>
                        <div className="search-input">
                           <Input.Search
                              placeholder="Search leads..."
                              allowClear
                              value={searchInput}
                              onSearch={handleSearch}
                              onChange={handleSearchInputChange}
                              prefix={<SearchOutlined className="text-gray-400" />}
                           />
                        </div>
                     </Col>
                     <Col xs={24} sm={12} md={5}>
                        <Select
                           placeholder="All Status"
                           className="w-full filter-select"
                           allowClear
                           value={statusFilter}
                           onChange={(v) => setStatusFilter(v || null)}
                        >
                           {statusOptions.map(status => (
                              <Select.Option key={status} value={status}>
                                 <div className="flex items-center gap-2">
                                    <div
                                       className="w-2 h-2 rounded-full"
                                       style={{ backgroundColor: statusConfig[status].color }}
                                    />
                                    {status}
                                 </div>
                              </Select.Option>
                           ))}
                        </Select>
                     </Col>
                     <Col xs={24} sm={12} md={5}>
                        <Select
                           placeholder="All Projects"
                           allowClear
                           className="w-full filter-select"
                           value={projectFilter}
                           onChange={(v) => setProjectFilter(v || null)}
                        >
                           {projects.map(p => (
                              <Select.Option key={p.id} value={p.id}>
                                 <div className="flex items-center justify-between w-full">
                                    <span>{p.name}</span>
                                    <span className="stats-badge">{p.lead_count}</span>
                                 </div>
                              </Select.Option>
                           ))}
                        </Select>
                     </Col>
                     <Col xs={24} sm={12} md={8}>
                        <Space wrap>
                           {user?.role === "admin" && (
                              <>
                                 <Button
                                    className={`action-btn ${assignmentFilter === "unassigned" ? 'bg-indigo-600 text-white hover:bg-indigo-700' : ''}`}
                                    onClick={() => setAssignmentFilter(assignmentFilter === "unassigned" ? null : "unassigned")}
                                 >
                                    <UserOutlined style={{ fontSize: '12px' }} />
                                    Unassigned
                                 </Button>
                                 {selectedRowKeys.length > 0 && (
                                    <Button
                                       type="primary"
                                       className="action-btn"
                                       onClick={() => setBulkAssignOpen(true)}
                                    >
                                       <TeamOutlined />
                                       Assign ({selectedRowKeys.length})
                                    </Button>
                                 )}
                              </>
                           )}
                           {hasActiveFilters && (
                              <Button
                                 className="action-btn"
                                 onClick={handleClearFilters}
                              >
                                 <ClearOutlined />
                                 Clear
                              </Button>
                           )}
                           <Button
                              className="action-btn"
                              onClick={() => {
                                 setLeads([]);
                                 setCurrentPage(1);
                                 setHasMore(true);
                                 fetchLeads(1, false);
                              }}
                           >
                              <ReloadOutlined />
                           </Button>
                        </Space>
                     </Col>
                  </Row>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                     <div className="flex gap-2">
                        {user?.role === "admin" && (
                           <>
                              <Upload
                                 accept=".xlsx,.xls"
                                 showUploadList={false}
                                 beforeUpload={handleImport}
                                 disabled={loading}
                              >
                                 <Button
                                    icon={<UploadOutlined />}
                                    className="action-btn"
                                    style={{ color: '#059669' }}
                                 >
                                    Import Excel
                                 </Button>
                              </Upload>
                           </>
                        )}
                        {(user?.role === "admin" || user?.role === "staff") && (
                           <Button
                              icon={<DownloadOutlined />}
                              onClick={handleExport}
                              loading={loading}
                              className="action-btn"
                              style={{ color: '#2563eb' }}
                           >
                              Export Excel
                           </Button>
                        )}

                        <DatePicker
                           value={fromDate}
                           onChange={(date) => setFromDate(date)}
                           format="DD-MM-YYYY"
                           placeholder="From Date"
                           allowClear
                        />

                        <DatePicker
                           value={toDate}
                           onChange={(date) => setToDate(date)}
                           format="DD-MM-YYYY"
                           placeholder="To Date"
                           allowClear
                        />
                     </div>
                     {/* {user?.role === "admin" && ( */}
                     <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleOpenAddModal}
                        className="action-btn"
                     >
                        Add New Lead
                     </Button>
                     {/* )} */}
                  </div>
               </div>
            </div>

            <div className="custom-card custom-table">
               <InfiniteScroll
                  dataLength={leads.length}
                  next={fetchMoreData}
                  hasMore={hasMore && !loading}
                  loader={
                     <div className="text-center py-4">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        <p className="text-gray-500 mt-2 text-sm">Loading more leads...</p>
                     </div>
                  }
                  endMessage={
                     leads.length > 0 && (
                        <div className="text-center py-4 text-gray-500 text-sm border-t">
                           <Divider plain>
                              ✓ All {total} leads loaded
                           </Divider>
                        </div>
                     )
                  }
                  height={500}
                  scrollThreshold={0.9}
               >
                  <Table
                     size="small"
                     rowSelection={user?.role === "admin" ? rowSelection : null}
                     dataSource={leads}
                     columns={columns}
                     rowKey="id"
                     loading={loading && currentPage === 1}
                     scroll={{ x: 1200 }}
                     pagination={false}
                     sticky
                     tableLayout="fixed"
                  />
               </InfiniteScroll>
            </div>
         </div>

         <Modal
            title={
               <div className="flex items-center gap-2 text-lg font-semibold">
                  {editingLead ? <EditOutlined /> : <PlusOutlined />}
                  {editingLead ? "Edit Lead" : "Add New Lead"}
               </div>
            }
            open={open}
            onCancel={handleCloseModal}
            footer={null}
            width={450}
            destroyOnClose
         >
            <Form
               layout="vertical"
               form={form}
               onFinish={handleSubmit}
               initialValues={{ status: "New" }}
               className="modal-form mt-4"
            >
               {user?.role === "admin" && (
                  <>
                     <Form.Item
                        name="name"
                        label="Lead Name"
                        rules={[{ required: true, message: "Name is required" }]}
                     >
                        <Input placeholder="Enter lead name" />
                     </Form.Item>
                     <Form.Item
                        name="phone"
                        label="Phone Number"
                        rules={[{ pattern: /^[0-9]{10,15}$/, message: "Invalid phone number" }]}
                     >
                        <Input placeholder="Enter phone number" />
                     </Form.Item>
                     {editingLead && (
                        <Form.Item name="project_id" label="Project">
                           <Select placeholder="Select project" allowClear>
                              {projects.map(p => (
                                 <Select.Option key={p.id} value={p.id}>{p.name}</Select.Option>
                              ))}
                           </Select>
                        </Form.Item>
                     )}
                     <Form.Item name="assigned_to" label="Assign To">
                        <Select placeholder="Select user" allowClear>
                           {usersList.map(u => (
                              <Select.Option key={u.id} value={u.id}>{u.name}</Select.Option>
                           ))}
                        </Select>
                     </Form.Item>
                  </>
               )}
               <Form.Item name="status" label="Status">
                  <Select>
                     {statusOptions.map(s => (
                        <Select.Option key={s} value={s}>
                           <div className="flex items-center gap-2">
                              <div
                                 className="w-2 h-2 rounded-full"
                                 style={{ backgroundColor: statusConfig[s].color }}
                              />
                              {s}
                           </div>
                        </Select.Option>
                     ))}
                  </Select>
               </Form.Item>
               <div className="flex justify-end gap-2 mt-6">
                  <Button onClick={handleCloseModal}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={loading}>
                     {editingLead ? "Update" : "Create"}
                  </Button>
               </div>
            </Form>
         </Modal>

         <Modal
            title={
               <div className="flex items-center gap-2 text-lg font-semibold">
                  <DollarOutlined />
                  Convert to Sale
               </div>
            }
            open={convertOpen}
            onCancel={handleCloseConvertModal}
            footer={null}
            destroyOnClose
         >
            <Form
               layout="vertical"
               form={convertForm}
               onFinish={handleConvert}
               className="modal-form mt-4"
            >
               <Form.Item
                  name="sale_amount"
                  label="Sale Amount"
                  rules={[{ required: true, message: "Amount is required" }]}
               >
                  <Input type="number" placeholder="Enter amount" min={1} prefix="₹" />
               </Form.Item>
               <Form.Item
                  name="closing_date"
                  label="Closing Date"
                  rules={[{ required: true, message: "Date is required" }]}
               >
                  <Input type="date" />
               </Form.Item>
               <div className="flex justify-end gap-2 mt-6">
                  <Button onClick={handleCloseConvertModal}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={loading}>
                     Convert
                  </Button>
               </div>
            </Form>
         </Modal>

         <Modal
            title={
               <div className="flex items-center gap-2 text-lg font-semibold">
                  <TeamOutlined />
                  Bulk Assign Leads
               </div>
            }
            open={bulkAssignOpen}
            onCancel={() => { setBulkAssignOpen(false); setBulkAssignUser(null); }}
            onOk={handleBulkAssign}
            okText="Assign"
         >
            <div className="py-4">
               <p className="text-gray-600 mb-4">
                  Assign <b>{selectedRowKeys.length} leads</b> to:
               </p>
               <Select
                  placeholder="Select user"
                  className="w-full"
                  size="large"
                  value={bulkAssignUser}
                  onChange={setBulkAssignUser}
               >
                  {usersList.map(u => (
                     <Select.Option key={u.id} value={u.id}>
                        <div className="flex items-center gap-2">
                           <Avatar size="small" style={{ backgroundColor: '#6366f1' }}>
                              {u.name?.charAt(0)?.toUpperCase()}
                           </Avatar>
                           {u.name}
                        </div>
                     </Select.Option>
                  ))}
               </Select>
            </div>
         </Modal>
      </div>
   );
};

export default Leads;